/**
 * ItemImpactTab
 *
 * Retrospective Cost of Delay impact log for a single item.
 * Lets users record real business consequences that occurred because the item
 * was deferred or deprioritized — revenue missed, incidents, churn, etc.
 *
 * Distinct from the predictive `cost_of_delay` attribute in ValuesTab.
 */

import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  TrendingDown,
  UserMinus,
  FileWarning,
  ShieldAlert,
  Megaphone,
  CircleDot,
  Plus,
  Trash2,
  ChevronUp,
  DollarSign,
  Loader2,
} from 'lucide-react';
import { useItemImpactStore, EMPTY_IMPACT_EVENTS } from '../../../stores/prioritization/itemImpactStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { knownActorName, useActorDisplayMap } from '../../../hooks/prioritization/useActorDisplayMap';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { getWorkspaceModules, isModuleEnabled } from '../../../lib/workspaceModulesService';
import {
  IMPACT_EVENT_TYPE_META,
  IMPACT_EVENT_TYPE_OPTIONS,
  EMPTY_IMPACT_EVENT_FORM,
} from '../../../types/prioritization/itemImpact';
import type { ImpactEventForm, ImpactEventType, ItemImpactEvent } from '../../../types/prioritization/itemImpact';

// ── Icon map ──────────────────────────────────────────────────────────────────

const EVENT_TYPE_ICONS: Record<ImpactEventType, React.ElementType> = {
  revenue_missed: TrendingDown,
  incident: AlertTriangle,
  churn: UserMinus,
  contract_risk: FileWarning,
  compliance_breach: ShieldAlert,
  reputation: Megaphone,
  other: CircleDot,
};

// ── Severity labels ───────────────────────────────────────────────────────────

const SEVERITY_LABELS: Record<number, string> = {
  1: 'Minor',
  2: 'Moderate',
  3: 'Significant',
  4: 'Major',
  5: 'Critical',
};

const SEVERITY_COLORS: Record<number, string> = {
  1: 'text-[var(--text-ter)]',
  2: 'text-[var(--mustard)]',
  3: 'text-[var(--mustard)]',
  4: 'text-[var(--coral)]',
  5: 'text-[var(--coral)] font-semibold',
};

function formatAmount(amount: number | undefined, currency: string): string | null {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

// ── Event card ────────────────────────────────────────────────────────────────

function ImpactEventCard({
  event,
  recordedByName,
  currentUserId,
  onDelete,
}: {
  event: ItemImpactEvent;
  /** The person who logged it, by name; nothing while the name is unread. */
  recordedByName: string | null;
  currentUserId?: string;
  onDelete: (id: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const meta = IMPACT_EVENT_TYPE_META[event.eventType];
  const Icon = EVENT_TYPE_ICONS[event.eventType];
  const canDelete = !currentUserId || event.recordedBy === currentUserId;
  const amountDisplay = formatAmount(event.impactAmount, event.impactCurrency);

  return (
    <div className="border border-[var(--rule)] p-4 bg-[var(--bg-raised)]">
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border ${meta.chipClass}`}>
          <Icon className="h-4 w-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${meta.chipClass}`}>
              {meta.label}
            </span>
            {event.severity != null && (
              <span className={`text-xs ${SEVERITY_COLORS[event.severity] ?? 'text-[var(--text-ter)]'}`}>
                {SEVERITY_LABELS[event.severity] ?? `Severity ${event.severity}`}
              </span>
            )}
            {amountDisplay && (
              <span className="inline-flex items-center gap-0.5 text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium bg-[var(--green-dim)] border border-[var(--green)] rounded-full px-2 py-0.5">
                <DollarSign className="h-3 w-3" />
                {amountDisplay}
              </span>
            )}
          </div>

          <p className="mt-1.5 text-sm text-[var(--text-pri)] leading-relaxed">{event.description}</p>

          <p className="mt-1 text-xs text-[var(--text-ter)]">
            Occurred {event.occurredAt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
            {recordedByName && <span> · Logged by {recordedByName}</span>}
          </p>
        </div>

        {canDelete && (
          <div className="flex-shrink-0">
            {confirming ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => { onDelete(event.id); setConfirming(false); }}
                  className="text-xs text-[var(--coral)] hover:text-[var(--coral)] font-medium"
                >
                  Delete
                </button>
                <span className="text-[var(--text-ter)]">·</span>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] rounded transition-colors"
                title="Delete this impact event"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add event form ────────────────────────────────────────────────────────────

function AddImpactEventForm({
  itemId,
  onCreated,
}: {
  itemId: string;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<ImpactEventForm>({ ...EMPTY_IMPACT_EVENT_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createEvent = useItemImpactStore((s) => s.createEvent);
  const currentUserId = useUserStore((s) => s.userId);

  const set = (patch: Partial<ImpactEventForm>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const validate = (): string | null => {
    if (!form.description.trim()) return 'Description is required.';
    if (!form.occurredAt) return 'Occurred date is required.';
    if (form.impactAmount && isNaN(parseFloat(form.impactAmount))) return 'Amount must be a number.';
    if (form.impactAmount && parseFloat(form.impactAmount) < 0) return 'Amount cannot be negative.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setError(null);
    setSaving(true);
    const result = await createEvent(itemId, form, currentUserId);
    setSaving(false);
    if (result) {
      setForm({ ...EMPTY_IMPACT_EVENT_FORM });
      onCreated();
    } else {
      setError('Failed to save. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 border border-[var(--rule)] p-4 bg-[var(--surface)]">
      <h3 className="text-sm font-semibold text-[var(--text-pri)]">Log an impact event</h3>

      {/* Event type + severity row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Event type</label>
          <select
            value={form.eventType}
            onChange={(e) => set({ eventType: e.target.value as ImpactEventType })}
            className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none"
          >
            {IMPACT_EVENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Severity (optional)</label>
          <select
            value={form.severity ?? ''}
            onChange={(e) => set({ severity: e.target.value ? Number(e.target.value) : undefined })}
            className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none"
          >
            <option value="">— not set —</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>{n} – {SEVERITY_LABELS[n]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
          What happened? <span className="text-[var(--coral)]">*</span>
        </label>
        <p className="text-xs text-[var(--text-ter)] mb-1">{IMPACT_EVENT_TYPE_META[form.eventType].description}</p>
        <textarea
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
          rows={3}
          placeholder="Describe the business impact that occurred because this item was not completed…"
          className="w-full text-sm border border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none resize-none"
        />
      </div>

      {/* Date + amount row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
            When did this occur? <span className="text-[var(--coral)]">*</span>
          </label>
          <input
            type="date"
            value={form.occurredAt}
            onChange={(e) => set({ occurredAt: e.target.value })}
            max={new Date().toISOString().split('T')[0]}
            className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Estimated impact amount (optional)</label>
          <div className="flex gap-1.5">
            <select
              value={form.impactCurrency}
              onChange={(e) => set({ impactCurrency: e.target.value })}
              className="text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none w-20"
            >
              {['USD', 'EUR', 'GBP', 'CAD', 'AUD'].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input
              type="number"
              min={0}
              step="any"
              placeholder="0"
              value={form.impactAmount}
              onChange={(e) => set({ impactAmount: e.target.value })}
              className="flex-1 text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none"
            />
          </div>
        </div>
      </div>

      {error && (
        <p className="text-sm text-[var(--coral)] flex items-center gap-1.5">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {saving ? 'Saving…' : 'Log event'}
        </button>
      </div>
    </form>
  );
}

// ── Summary banner ────────────────────────────────────────────────────────────

function ImpactSummaryBanner({ itemId }: { itemId: string }) {
  // Subscribe to primitives — never returns new objects, so Zustand's Object.is
  // comparison is stable and cannot produce an infinite re-render loop.
  const count = useItemImpactStore((s) => (s.eventsByItem[itemId] ?? EMPTY_IMPACT_EVENTS).length);
  const totalAmount = useItemImpactStore((s) => {
    const evts = s.eventsByItem[itemId] ?? EMPTY_IMPACT_EVENTS;
    const withAmounts = evts.filter((e) => e.impactAmount != null);
    return withAmounts.length > 0
      ? withAmounts.reduce((sum, e) => sum + (e.impactAmount ?? 0), 0)
      : null;
  });
  const currency = useItemImpactStore(
    (s) => (s.eventsByItem[itemId] ?? EMPTY_IMPACT_EVENTS)[0]?.impactCurrency ?? 'USD',
  );
  const highestSeverity = useItemImpactStore((s) => {
    const severities = (s.eventsByItem[itemId] ?? EMPTY_IMPACT_EVENTS)
      .map((e) => e.severity)
      .filter((sv): sv is number => sv != null);
    return severities.length > 0 ? Math.max(...severities) : null;
  });

  if (count === 0) return null;

  const amountDisplay = totalAmount != null ? formatAmount(totalAmount, currency) : null;

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm">
      <span className="font-medium text-[var(--mustard)]">
        {count} impact event{count !== 1 ? 's' : ''} recorded
      </span>
      {amountDisplay && (
        <span className="text-[var(--mustard)]">
          Total estimated: <span className="font-semibold">{amountDisplay}</span>
        </span>
      )}
      {highestSeverity != null && (
        <span className={`${SEVERITY_COLORS[highestSeverity] ?? 'text-[var(--text-sec)]'}`}>
          Highest severity: {SEVERITY_LABELS[highestSeverity] ?? highestSeverity}
        </span>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export interface ItemImpactTabProps {
  itemId: string;
}

export function ItemImpactTab({ itemId }: ItemImpactTabProps) {
  const fetchForItem = useItemImpactStore((s) => s.fetchForItem);
  // Subscribe directly to the events array for reactivity on optimistic updates/deletes.
  // `EMPTY_IMPACT_EVENTS` is a stable reference so Zustand's Object.is check holds
  // when no events are loaded yet — prevents spurious re-renders.
  const events = useItemImpactStore((s) => s.eventsByItem[itemId] ?? EMPTY_IMPACT_EVENTS);
  const deleteEvent = useItemImpactStore((s) => s.deleteEvent);
  // The person who logged each event, by name (seen on production 2026-11-02:
  // the card printed the first eight characters of their id).
  const recordedByNames = useActorDisplayMap(events.map((e) => e.recordedBy));
  const fetchedItemIds = useItemImpactStore((s) => s.fetchedItemIds);
  const currentUserId = useUserStore((s) => s.userId);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [moduleEnabled, setModuleEnabled] = useState(false);
  const [moduleChecked, setModuleChecked] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    getWorkspaceModules(workspaceId).then(({ data }) => {
      if (!cancelled) {
        setModuleEnabled(isModuleEnabled(data, 'cost_of_delay'));
        setModuleChecked(true);
      }
    });
    return () => { cancelled = true; };
  }, [workspaceId]);

  useEffect(() => {
    if (!moduleEnabled) return;
    if (fetchedItemIds.has(itemId)) return;
    setLoading(true);
    fetchForItem(itemId).finally(() => setLoading(false));
  }, [itemId, fetchForItem, fetchedItemIds, moduleEnabled]);

  if (!moduleChecked || !moduleEnabled) return null;

  return (
    <div className="space-y-4">
      {/* Summary banner — only visible when events exist */}
      <ImpactSummaryBanner itemId={itemId} />

      {/* Header row with "Log event" toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">Cost of Delay — Impact Log</h3>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Record real business consequences that occurred because this item was deferred or not delivered.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200 transition-colors"
        >
          {showForm ? (
            <><ChevronUp className="h-4 w-4" /> Cancel</>
          ) : (
            <><Plus className="h-4 w-4" /> Log event</>
          )}
        </button>
      </div>

      {/* Collapsible form */}
      {showForm && (
        <AddImpactEventForm
          itemId={itemId}
          onCreated={() => setShowForm(false)}
        />
      )}

      {/* Event list */}
      {loading ? (
        <div className="flex items-center gap-2 py-4 text-[var(--text-ter)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading impact events…</span>
        </div>
      ) : events.length === 0 ? (
        <div className="py-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--hover-tint)] mb-3">
            <TrendingDown className="h-6 w-6 text-[var(--text-ter)]" />
          </div>
          <p className="text-sm font-medium text-[var(--text-sec)]">No impact events logged</p>
          <p className="text-xs text-[var(--text-ter)] mt-1 max-w-xs mx-auto">
            When deferring this item causes a real business consequence — a missed deal, an incident, churn — log it here. This data calibrates future cost of delay estimates.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <ImpactEventCard
              key={event.id}
              event={event}
              recordedByName={knownActorName(recordedByNames, event.recordedBy)}
              currentUserId={currentUserId}
              onDelete={(id) => deleteEvent(id, itemId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
